import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Menu, MenuItem, Popover } from '@workboard/wobo-ui';
import Add from '@material-ui/icons/Add';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import BarChart from '@material-ui/icons/BarChart';
import Waves from '@material-ui/icons/Waves';
import PersonOutlined from '@material-ui/icons/PersonOutlined';
import SvgIcon from '@material-ui/core/SvgIcon';
import { useTranslation } from 'react-i18next';
import { ReadOnlyComp } from '../../../../read-license/index';
import KpiIcon from '../../../../vectors/kpi.svg';
import { KPI_LOCATION_VALUES } from '../../../kpis/constants';
import { openCreateKpiModal } from '../../../kpis/utils';

export const redirectToUrl = (url: string): void => {
  window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

export type AddMenuProps = {
  teamId: number;
  showAddOkr: boolean;
  showAddWorkstream: boolean;
  readOnlyePermission: boolean;
  readOnlyFeatureFlag: boolean;
  teamType: number;
  teamRole: number;
};

const AddMenuDiv = styled('div')`
  padding: 5px;
  position: relative;
`;

const ButtonWrapper = styled(Button)(
  ({ disabled }: any) =>
    `${
      disabled
        ? `
    cursor: default;
    border:none;
    color:#FFFFFF;
    background: #b7d6e5 !important;
  `
        : `
    &:focus:not(:focus-visible):not(:hover) {
      background: #005f8f !important;
    }
  `
    }`,
);
const MenuItemWrapper = styled(MenuItem)`
  gap: 8px;
  display: flex;
  align-items: center;
`;

export const AddMenu = ({
  teamId,
  showAddOkr,
  showAddWorkstream,
  readOnlyePermission,
  readOnlyFeatureFlag,
  teamType,
  teamRole,
}: AddMenuProps): JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isRead, setIsRead] = useState(false);
  const [rolEvent, setRolEvent] = useState(null);
  const createButtonRef = useRef();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const page = new URL(window.location.href).pathname;
  const showAddKpi = window.wbConfig.isKpisEnabled ?? false;
  const toggleMenuOpen = (e): void => {
    if (readOnlyePermission && readOnlyFeatureFlag) {
      setIsRead(true);
      setRolEvent({
        rolEventTarget: 'Add Menu',
        rolEventType: 'Click',
        rolEventSource: page,
      });
    } else if (isOpen) {
      setIsOpen(false);
      setAnchorEl(null);
    } else {
      setIsOpen(true);
      setAnchorEl(e.currentTarget);
    }
  };
  const closeMenu = (): void => {
    setIsOpen(false);
    setRolEvent(null);
    setAnchorEl(null);
  };
  const addTeamOkr = (): void => {
    window.openAddEditObjectivePopup({ tid: teamId });
    closeMenu();

    window.wb_track('OKR_CREATE', {
      UserId: window.uData.user_id,
      OrgId: window.uData.org_id,
      Timestamp: Date.now(),
      Source: 'Team',
      Type: 'Team',
    });
  };
  const addTeamKpi = (): void => {
    openCreateKpiModal(KPI_LOCATION_VALUES.TEAM_PAGE_ADD);

    closeMenu();

    window.wb_track('KPI_CREATE_CLICKED', {
      UserId: window.uData.user_id,
      OrgId: window.uData.org_id,
      Timestamp: Date.now(),
      Source: 'Team',
      Type: 'Team',
    });
  };

  const addTeamWorkstream = (): void => {
    $.post('/wb/tag/addWorksream', { teamId, columnId: 0 }, (data: never) => {
      window.openCreateWsDialog(data);
    });
    closeMenu();
  };
  const addTeamMember = (): void => {
    const url = `/wb/team/AddTeamMembers?tid=${teamId}`;
    $(document).scrollTop(0);
    if (!$(window.team_dialog).hasClass('ui-dialog-content')) {
      window.loadTeamPopupBoxes();
    }
    window.team_dialog.html('');

    $.post(url)
      .done((data: string) => {
        window.team_dialog.html(data);
      })
      .always(() => {
        window.team_dialog.dialogInit();
        window.team_dialog.dialog('option', 'top', 45);
        window.team_dialog.dialog(
          'option',
          'dialogClass',
          'add-team-member-popup',
        );
        window.team_dialog.dialog('option', 'width', 600);
        window.team_dialog.dialog('option', 'position', ['auto', 15]);
        window.team_dialog.dialog('open');
      });
    closeMenu();

    window.wb_track('TEAM_MEMBER_ADD', {
      UserId: window.uData.user_id,
      OrgId: window.uData.org_id,
      Timestamp: Date.now(),
      Source: 'Team',
      Type: 'Team',
    });
  };

  const handleButtonClose = async () => {
    setIsRead(false);
  };
  const teamOkrLabel =
    window.wbConfig.default_org_locale === 'locale_8'
      ? t('Add Team Outcome')
      : t('Add Team OKR');
  const isTeamSettingsRestrictions =
    window.wbConfig.isTeamSettingsRestrictions ?? false;
  const managerCoManagerCheck =
    teamType !== 2
      ? window.wbConfig.addTeamMemManagerCoManagerFunCheck
      : window.wbConfig.addTeamMemManagerCoManagerDynamicCheck;
  const orgAdminCheck =
    teamType !== 2
      ? window.wbConfig.addTeamMemOrgAdminFunCheck
      : window.wbConfig.addTeamMemOrgAdminDynamicCheck;
  // CORE-13007: Team Admin (teamRole === 4) was missing from the role-parent
  // and "Add Team Member" gating when Team Settings restrictions are on. Without
  // this, Team Admins never saw the "+ Add Team Member" menu item.
  const teamAdminAddCheck =
    teamType !== 2
      ? window.wbConfig.addTeamMemTeamAdminFunCheck
      : window.wbConfig.addTeamMemTeamAdminDynamicCheck;
  const isTeamAdminOnTeam = teamRole === 4;
  const roleParent = isTeamSettingsRestrictions
    ? teamRole === 1 || teamRole === 3
    : teamRole === 1 || teamRole === 3 || teamRole === 5;
  const roleCheckTeamMember = isTeamSettingsRestrictions
    ? orgAdminCheck ||
      (roleParent && managerCoManagerCheck) ||
      (isTeamAdminOnTeam && teamAdminAddCheck)
    : roleParent;

  return (
    <AddMenuDiv>
      <ButtonWrapper
        variant="primary"
        onClick={toggleMenuOpen}
        disabled={readOnlyePermission && readOnlyFeatureFlag}
        ref={createButtonRef}
      >
        <Add /> {t('Add')} <KeyboardArrowDown />
      </ButtonWrapper>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        id="teamsAddMenu"
        keepMounted
        onClose={closeMenu}
        open={isOpen}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        {showAddOkr ? (
          <MenuItemWrapper onClick={addTeamOkr}>
            <BarChart />
            {teamOkrLabel}
          </MenuItemWrapper>
        ) : null}
        {showAddKpi ? (
          <MenuItemWrapper onClick={addTeamKpi}>
            <SvgIcon component={KpiIcon} addlClasses="icon-kpi" />
            {t('Add Team KPI')}
          </MenuItemWrapper>
        ) : null}
        {showAddWorkstream ? (
          <MenuItemWrapper onClick={addTeamWorkstream}>
            <Waves /> {t('Add Team Workstream')}
          </MenuItemWrapper>
        ) : null}
        {roleCheckTeamMember ? (
          <MenuItemWrapper onClick={addTeamMember}>
            <PersonOutlined /> {t('Add Team Member')}
          </MenuItemWrapper>
        ) : null}
      </Menu>
      {isRead && (
        <div style={{ position: 'absolute' }}>
          <Popover
            targetRef={createButtonRef}
            removeActions
            removeClose
            arrow={false}
            placement="bottom-start"
            open={isRead}
            onDismiss={() => setIsRead(false)}
            className="readOnlyOver"
            content={
              <ReadOnlyComp
                handleClose={handleButtonClose}
                rolEvent={rolEvent}
              />
            }
          />
        </div>
      )}
    </AddMenuDiv>
  );
};

export default AddMenu;
